import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import { AppSearch, JobChainData, JobListData } from '@/types';
import { saveAs } from 'file-saver-es';
import { QUERY_KEYS } from '../../queryKeys';
import { jobs } from '../Jobs';

const serializeJobChainsQuery = (q: AppSearch<JobChainData>) =>
  JSON.stringify({
    name: q.name,
    search: q.search,
    page: q.page,
    pageSize: q.pageSize,
    sortField: q.sortField,
    sortOrder: q.sortOrder,
    filters: q.filters,
  });

const prepareJobChainsSearchBody = (
  queryData: AppSearch<JobChainData>,
): AppSearch<JobChainData> => {
  const body: AppSearch<JobChainData> = JSON.parse(JSON.stringify(queryData));
  body.filters?.forEach((filter) => {
    filter.value = filter.value.map((v) =>
      v instanceof Date ? v.toISOString() : v?.toString(),
    );
  });
  return body;
};

const invalidateJobChainQueries = () =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.jobChainsList],
    }),
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.jobChains],
    }),
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.jobChainDag],
    }),
  ]);

export const useChainJobs = (query: AppSearch<JobChainData> | undefined) => {
  const serialized = useMemo(
    () => (query ? serializeJobChainsQuery(query) : '__idle__'),
    [query],
  );

  return useQuery({
    queryKey: [QUERY_KEYS.jobChainsList, serialized] as const,
    queryFn: async () => {
      const body = prepareJobChainsSearchBody(query!);
      const response = await jobs.getJobChains(body);
      return response?.data;
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
  });
};

export const fetchJobChainsForExport = async (
  query: AppSearch<JobChainData>,
): Promise<JobChainData[]> => {
  const exportQuery = { ...query };
  exportQuery.page = undefined;
  exportQuery.pageSize = undefined;
  const body = prepareJobChainsSearchBody(
    exportQuery as AppSearch<JobChainData>,
  );
  const response = await jobs.getJobChains(body);
  return response?.data?.entities ?? [];
};

export const invalidateJobChainsList = () =>
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.jobChainsList],
  });

const serializeJobsQuery = (q: AppSearch<JobListData>) =>
  JSON.stringify({
    name: q.name,
    search: q.search,
    page: q.page,
    pageSize: q.pageSize,
    sortField: q.sortField,
    sortOrder: q.sortOrder,
    filters: q.filters,
  });

const prepareJobsSearchBody = (
  queryData: AppSearch<JobListData>,
): AppSearch<JobListData> => {
  const body: AppSearch<JobListData> = JSON.parse(JSON.stringify(queryData));
  body.filters?.forEach((filter) => {
    filter.value = filter.value.map((v) =>
      v instanceof Date ? v.toISOString() : v?.toString(),
    );
  });
  return body;
};

export const useJobs = (query: AppSearch<JobListData> | undefined) => {
  const serialized = useMemo(
    () => (query ? serializeJobsQuery(query) : '__idle__'),
    [query],
  );

  return useQuery({
    queryKey: [QUERY_KEYS.jobsList, serialized] as const,
    queryFn: async () => {
      const body = prepareJobsSearchBody(query!);
      const response = await jobs.getJobs(body);
      return response?.data;
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
  });
};

export const fetchJobsForExport = async (
  query: AppSearch<JobListData>,
): Promise<JobListData[]> => {
  const exportQuery = { ...query };
  exportQuery.page = undefined;
  exportQuery.pageSize = undefined;
  const body = prepareJobsSearchBody(exportQuery as AppSearch<JobListData>);
  const response = await jobs.getJobs(body);
  return response?.data?.entities ?? [];
};

export const downloadJobsExport = async (
  query: AppSearch<JobListData>,
  format: 'xlsx' | 'csv',
) => {
  const exportQuery = { ...query };
  exportQuery.page = undefined;
  exportQuery.pageSize = undefined;
  const body = prepareJobsSearchBody(exportQuery as AppSearch<JobListData>);
  const response = await jobs.exportJobs({ ...body, format });
  if (!response?.success || !response.data) return;
  saveAs(response.data, format === 'csv' ? 'Jobs.zip' : 'Jobs.xlsx');
};

export const useJobChainDetails = (jobChainId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.jobChains, jobChainId],
    queryFn: async () => (await jobs.getJobChainDetails(jobChainId)).data,
  });

export const useJobChainDag = (jobChainId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.jobChainDag, jobChainId],
    queryFn: async () => (await jobs.getJobChainDag(jobChainId)).data,
    enabled: !!jobChainId,
  });

export const useJobChainFilters = () =>
  useQuery({
    queryKey: [QUERY_KEYS.jobChainFilters],
    queryFn: async () => (await jobs.getJobChainFilters()).data,
  });

export const useCreateJob = () =>
  useMutation({
    mutationFn: jobs.createJob,
    onSuccess: () => invalidateJobChainQueries(),
  });

export const useCreateLoadPullRequestJob = (options?: {
  siteId?: number;
  customerId?: number;
}) =>
  useMutation({
    mutationFn: jobs.createLoadPullRequestJob,
    onSuccess: (response) => {
      if (!response.success) return;
      invalidateJobChainQueries();
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.loadLanes, options?.siteId, options?.customerId],
      });
    },
  });

export const useUpdateJobStatus = () =>
  useMutation({
    mutationFn: jobs.updateJobStatus,
    onSuccess: () => invalidateJobChainQueries(),
  });

export const useUpdateJobItemStatus = () =>
  useMutation({
    mutationFn: jobs.updateJobItemStatus,
    onSuccess: () => invalidateJobChainQueries(),
  });

export const useJobTypes = () =>
  useQuery({
    queryKey: [QUERY_KEYS.jobTypes],
    queryFn: async () => (await jobs.getJobTypes()).data,
  });

export const useJobStatus = () =>
  useQuery({
    queryKey: [QUERY_KEYS.jobStatus],
    queryFn: async () => (await jobs.getJobStatus()).data,
  });

export const useJobTags = () =>
  useQuery({
    queryKey: [QUERY_KEYS.jobTags],
    queryFn: async () => (await jobs.getJobTags()).data,
  });

export const useCreateJobTag = () =>
  useMutation({
    mutationFn: jobs.createJobTag,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.jobTags] }),
  });

export const useUpdateJobTag = () =>
  useMutation({
    mutationFn: jobs.updateJobTag,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.jobTags] }),
  });

export const useDeleteJobTag = () =>
  useMutation({
    mutationFn: jobs.deleteJobTag,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.jobTags] }),
  });

export const useJobTemplates = () =>
  useQuery({
    queryKey: [QUERY_KEYS.jobTemplates],
    queryFn: async () => (await jobs.getJobTemplates()).data,
  });

export const useJobItemTemplates = (jobTemplateId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.jobTemplates, jobTemplateId],
    queryFn: async () => (await jobs.getJobItemTemplates(jobTemplateId)).data,
    enabled: !!jobTemplateId,
  });
